import { damageSourceLabel } from '../data/damageSources.js';

/**
 * Tallies what hurt the player over a run, so the run-end screen can answer
 * the question a death currently teaches nothing about: *what killed me?*
 * (ARCHIVE.md P4, "death recap").
 *
 * Rides `player:damaged`, which Player only emits for a hit that actually
 * landed — dodges, i-framed hits, and absorbed armor plates all bail before
 * the emit, so the ledger counts real HP lost and nothing else. The amount it
 * records is the post-mitigation number for the same reason.
 *
 * Deliberately holds no reference to Game: it's a pure listener over the event
 * bus, which is what makes the killing-blow rule ("last source to land a hit")
 * correct without any knowledge of stages, bosses, or run flow.
 */
export class DamageLedger {
  constructor(eventBus) {
    // source id -> { damage, hits }. Insertion order is irrelevant; the recap
    // sorts by damage before rendering.
    this._bySource = new Map();
    this._killedBy = null;
    this._lastSource = null;
    eventBus.on('player:damaged', ({ amount, source }) => this._record(amount, source));
    // player:died carries the source of the fatal hit directly. Recorded
    // separately from the tally because the biggest damage source over a whole
    // run is usually NOT the thing that landed the last hit, and the recap
    // wants to say both.
    eventBus.on('player:died', ({ source } = {}) => {
      this._killedBy = source ?? this._lastSource ?? null;
    });
  }

  _record(amount, source) {
    if (!(amount > 0)) return;
    this._lastSource = source ?? null;
    // null/unknown sources are still tallied, under one shared bucket, rather
    // than dropped — a recap whose rows don't add up to the damage taken is
    // worse than one that admits an unattributed remainder.
    const key = source ?? 'unknown';
    const entry = this._bySource.get(key) ?? { damage: 0, hits: 0 };
    entry.damage += amount;
    entry.hits += 1;
    this._bySource.set(key, entry);
  }

  /**
   * The recap payload: every source that landed a hit, biggest first, plus
   * what struck the killing blow. Returns null when nothing ever hit the
   * player — a flawless run has no recap to show.
   */
  getRecap() {
    if (this._bySource.size === 0) return null;
    const rows = [...this._bySource.entries()]
      .map(([source, { damage, hits }]) => ({
        source, label: damageSourceLabel(source), damage: Math.round(damage), hits,
      }))
      .sort((a, b) => b.damage - a.damage);
    const totalDamage = rows.reduce((sum, row) => sum + row.damage, 0);
    return {
      rows,
      totalDamage,
      killedBy: this._killedBy ? damageSourceLabel(this._killedBy) : null,
    };
  }
}
